import React from 'react';
import { tokens } from '../theme/tokens';
import { goFullReload } from '../utils/nav';
import { BRAND_NAME } from '../config/brand';

// This page satisfies Meta's "Data Deletion Instructions URL" requirement.
// Meta calls this URL when a user requests deletion via Facebook/Instagram settings.
// URL to register in Meta App Dashboard: https://social.drupi.io/data-deletion

const Step: React.FC<{ number: number; title: string; children: React.ReactNode }> = ({ number, title, children }) => (
  <div style={{ display: 'flex', gap: 20, marginBottom: 28 }}>
    <div style={{
      flexShrink: 0,
      width: 36, height: 36, borderRadius: '50%',
      background: tokens.colors.luzu.gradient,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 15, color: 'white'
    }}>
      {number}
    </div>
    <div>
      <div style={{ fontWeight: 600, color: tokens.colors.primary.neutral[100], fontSize: 15, marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ color: tokens.colors.primary.neutral[400], fontSize: 14, lineHeight: 1.7 }}>
        {children}
      </div>
    </div>
  </div>
);

export const DataDeletionPage: React.FC = () => {
  return (
    <div style={{ background: tokens.colors.primary.neutral[1030], minHeight: '100vh' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '60px 24px 80px' }}>

        {/* Logo */}
        <div
          onClick={() => goFullReload('/')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 48 }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: tokens.colors.luzu.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/>
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/>
            </svg>
          </div>
          <span className="luzu-gradient-text" style={{ fontSize: 20, fontWeight: 700 }}>{BRAND_NAME}</span>
        </div>

        <h1 style={{ fontSize: 28, fontWeight: 700, color: tokens.colors.primary.neutral[50], margin: '0 0 8px' }}>
          Eliminar tus datos
        </h1>
        <p style={{ color: tokens.colors.primary.neutral[500], fontSize: 14, marginBottom: 40 }}>
          Instrucciones para solicitar la eliminación de los datos que {BRAND_NAME} almacena asociados
          a tu cuenta de Instagram o Facebook.
        </p>

        {/* What data we have */}
        <div style={{
          padding: '20px 24px', borderRadius: 12, marginBottom: 36,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ fontWeight: 600, color: tokens.colors.primary.neutral[100], marginBottom: 12, fontSize: 15 }}>
            ¿Qué datos almacena {BRAND_NAME} de tu cuenta de Instagram?
          </div>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {[
              'Tu ID de usuario de Instagram (identificador único)',
              'Tu nombre de usuario e imagen de perfil',
              'Métricas numéricas (vistas, likes, comentarios) de publicaciones vinculadas a campañas activas',
              'El token de acceso OAuth necesario para leer esas métricas'
            ].map((item, i) => (
              <li key={i} style={{ color: tokens.colors.primary.neutral[400], fontSize: 14, lineHeight: 1.7, marginBottom: 4 }}>
                {item}
              </li>
            ))}
          </ul>
          <p style={{ color: tokens.colors.primary.neutral[500], fontSize: 13, margin: '12px 0 0' }}>
            No almacenamos contraseñas, mensajes privados, listas de seguidores ni contenido audiovisual.
          </p>
        </div>

        {/* Steps */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontWeight: 600, color: tokens.colors.primary.neutral[100], fontSize: 16, marginBottom: 24 }}>
            Cómo solicitar la eliminación
          </div>

          <Step number={1} title="Desconectá tu cuenta desde la plataforma">
            Si tenés acceso a tu cuenta, ingresá a{' '}
            <a href="/connections" style={{ color: tokens.colors.luzu.orange }}>Mis redes</a>,
            hacé click en el ícono de eliminar junto a tu cuenta de Instagram y confirmá la desconexión.
            Esto revoca el token de acceso inmediatamente.
          </Step>

          <Step number={2} title='Envianos un email a privacidad@drupi.io'>
            Escribí desde cualquier dirección con el asunto{' '}
            <strong style={{ color: tokens.colors.primary.neutral[200] }}>"Eliminar mis datos — Instagram"</strong>{' '}
            e incluí tu nombre de usuario de Instagram (ej: @tuusuario).
            Con eso podemos identificar tu registro en nuestra base de datos.
          </Step>

          <Step number={3} title="Procesamos tu pedido en 30 días">
            Confirmamos la recepción del pedido por email y eliminamos todos los datos asociados
            a tu ID de Instagram dentro de los 30 días corridos. Te enviamos una confirmación cuando esté listo.
          </Step>
        </div>

        {/* Also revoke from Instagram */}
        <div style={{
          padding: '20px 24px', borderRadius: 12, marginBottom: 36,
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)'
        }}>
          <div style={{ fontWeight: 600, color: tokens.colors.primary.neutral[100], marginBottom: 8, fontSize: 14 }}>
            También podés revocar el acceso directamente desde Instagram
          </div>
          <p style={{ color: tokens.colors.primary.neutral[400], fontSize: 14, margin: 0, lineHeight: 1.7 }}>
            Andá a <strong style={{ color: tokens.colors.primary.neutral[200] }}>
            Configuración de Instagram → Seguridad → Apps y sitios web
            </strong> y eliminá el acceso de {BRAND_NAME}. Esto revoca el token de acceso desde el lado de Meta,
            independientemente del proceso de eliminación de datos en nuestros servidores.
          </p>
        </div>

        {/* Contact */}
        <div style={{
          padding: '24px', borderRadius: 12,
          background: 'rgba(255,95,46,0.06)', border: '1px solid rgba(255,95,46,0.2)'
        }}>
          <p style={{ margin: 0, color: tokens.colors.primary.neutral[300], fontSize: 15 }}>
            <strong style={{ color: tokens.colors.primary.neutral[50] }}>Contacto de privacidad</strong><br />
            <a href="mailto:privacidad@drupi.io" style={{ color: tokens.colors.luzu.orange }}>
              privacidad@drupi.io
            </a>
            {' '}— respondemos dentro de los 5 días hábiles.
          </p>
        </div>
      </div>
    </div>
  );
};
